import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import Page from 'src/components/Page';
import NotFoundCard from 'src/components/NotFoundCard';
import CustomDialog from 'src/components/CustomDialog';
import Support from 'src/api/support';
import alertSnackbarActions from 'src/store/actions/alertSnackbarActions';

const STATUS_LABELS = {
  open: 'Open',
  awaiting_user: 'Awaiting User',
  resolved: 'Resolved'
};

const STATUS_COLORS = {
  open: 'secondary',
  awaiting_user: 'default',
  resolved: 'primary'
};

const SENDER_LABELS = {
  user: 'Customer',
  ai: 'AI Assistant',
  admin: 'Support'
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  thread: {
    maxHeight: 480,
    overflowY: 'auto',
    padding: theme.spacing(1)
  },
  message: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    maxWidth: '85%'
  },
  user: {
    backgroundColor: theme.palette.background.dark
  },
  ai: {
    backgroundColor: '#eef1f6'
  },
  admin: {
    backgroundColor: '#e7f0ff',
    marginLeft: 'auto'
  },
  meta: {
    marginBottom: theme.spacing(2)
  }
}));

const formatDate = value => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const SupportTicketDetailView = ({ setOpen, setMessage, setSeverity }) => {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Mirror in-flight reply/close state into refs so the polling interval (which
  // closes over its initial render) can read the current values without being
  // re-created on every keystroke / state change.
  const sendingRef = useRef(false);
  const closingRef = useRef(false);
  const threadRef = useRef(null);
  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);
  useEffect(() => {
    closingRef.current = closing;
  }, [closing]);

  const notify = (severity, message) => {
    setSeverity(severity);
    setMessage(message);
    setOpen(true);
  };

  const loadTicket = active => {
    setLoading(true);
    Support.getTicket(id)
      .then(res => {
        if (active !== false) setTicket(res.data.data);
      })
      .catch(() => {
        if (active !== false) setTicket(null);
      })
      .finally(() => {
        if (active !== false) setLoading(false);
      });
  };

  useEffect(() => {
    let active = true;
    loadTicket(active);
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Near-real-time silent auto-refresh while viewing a ticket. Re-fetches the
  // ticket every 7s WITHOUT toggling the full-page spinner, so new customer
  // messages appear without clobbering the reply text or flickering the page.
  // `id` is in the deps so the interval is recreated (and stale closures
  // avoided) whenever the URL ticket changes.
  useEffect(() => {
    const POLL_MS = 7000;
    let cancelled = false;

    const poll = () => {
      // Skip background polls while the tab is hidden, or while the admin is
      // sending a reply / closing the ticket (those handlers own the state).
      if (document.hidden || sendingRef.current || closingRef.current) return;
      Support.getTicket(id)
        .then(res => {
          if (!cancelled) setTicket(res.data.data);
        })
        .catch(() => {
          /* swallow background-poll errors; the foreground load owns errors */
        });
    };

    const intervalId = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep the conversation pinned to the newest message: scroll the thread to
  // the bottom whenever the message count changes (initial load, a polled-in
  // customer reply, or the admin's own sent reply).
  const messageCount = ticket && ticket.messages ? ticket.messages.length : 0;
  useEffect(() => {
    const el = threadRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messageCount]);

  const handleReply = () => {
    if (!reply.trim()) {
      notify('error', 'Reply cannot be empty');
      return;
    }
    setSending(true);
    Support.reply(id, reply.trim())
      .then(res => {
        setTicket(res.data.data);
        setReply('');
        notify('success', 'Reply sent — the customer has been emailed');
      })
      .catch(err =>
        notify('error', err?.response?.data?.errors?.[0] || 'Failed to send reply')
      )
      .finally(() => setSending(false));
  };

  const confirmClose = () => {
    setDialogOpen(false);
    setClosing(true);
    Support.close(id)
      .then(res => {
        setTicket(res.data.data);
        notify('success', 'Ticket resolved');
      })
      .catch(err =>
        notify('error', err?.response?.data?.errors?.[0] || 'Failed to close ticket')
      )
      .finally(() => setClosing(false));
  };

  if (loading) {
    return (
      <Page className={classes.root} title="Support Ticket">
        <Box textAlign="center" py={10}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (!ticket) {
    return (
      <NotFoundCard
        title="Support Ticket"
        message="Ticket not found."
        backLabel="Back to inbox"
        onBack={() => navigate('/app/support/tickets')}
      />
    );
  }

  const isResolved = ticket.status === 'resolved';

  return (
    <Page className={classes.root} title={`Ticket ${ticket.reference}`}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          {/* Left: ticket metadata */}
          <Grid item lg={4} md={5} xs={12}>
            <Card>
              <CardHeader title={ticket.reference} subheader={ticket.subject} />
              <Divider />
              <CardContent>
                <Box className={classes.meta}>
                  <Chip
                    color={STATUS_COLORS[ticket.status] || 'default'}
                    label={STATUS_LABELS[ticket.status] || ticket.status}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Requester
                </Typography>
                <Typography gutterBottom>{ticket.name || '—'}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography gutterBottom>{ticket.email}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Source
                </Typography>
                <Typography gutterBottom>{ticket.source}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Opened
                </Typography>
                <Typography gutterBottom>{formatDate(ticket.createdAt)}</Typography>

                <Box mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    disabled={isResolved || closing}
                    onClick={() => setDialogOpen(true)}
                  >
                    {isResolved ? 'Resolved' : 'Close ticket'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: conversation + reply */}
          <Grid item lg={8} md={7} xs={12}>
            <Card>
              <CardHeader title="Conversation" />
              <Divider />
              <CardContent>
                <Box className={classes.thread} ref={threadRef}>
                  {ticket.messages.map(m => (
                    <Paper
                      key={m.id}
                      elevation={0}
                      className={`${classes.message} ${classes[m.sender] || ''}`}
                    >
                      <Typography variant="caption" color="textSecondary">
                        {SENDER_LABELS[m.sender] || m.sender}
                        {m.authorName ? ` · ${m.authorName}` : ''} ·{' '}
                        {formatDate(m.createdAt)}
                      </Typography>
                      <Typography variant="body1">{m.text}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Divider />
                <Box mt={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    label="Reply to customer"
                    placeholder={
                      isResolved
                        ? 'This ticket is resolved'
                        : 'Type your reply — the customer will get it by email'
                    }
                    value={reply}
                    disabled={isResolved || sending}
                    onChange={e => setReply(e.target.value)}
                  />
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isResolved || sending}
                      onClick={handleReply}
                    >
                      {sending ? 'Sending…' : 'Send reply'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <CustomDialog
        open={dialogOpen}
        title="Close this ticket?"
        message="Closing marks the ticket as resolved. You can still see it under the Resolved tab."
        handleDialogClose={() => setDialogOpen(false)}
        action={confirmClose}
      />
    </Page>
  );
};

SupportTicketDetailView.propTypes = {
  setOpen: PropTypes.func,
  setMessage: PropTypes.func,
  setSeverity: PropTypes.func
};

const { setOpen, setMessage, setSeverity } = alertSnackbarActions;

const mapDispatchToProps = {
  setOpen,
  setMessage,
  setSeverity
};

export default connect(null, mapDispatchToProps)(SupportTicketDetailView);
